// Copyright 2026. WebPros International GmbH. All rights reserved.

import { HighAvailabilityStatus } from 'common/api/resources/HighAvailability';
import React from 'react';
import {
    Action,
    Icon,
    Text,
    Translate,
} from '@plesk/ui-library';
import {
    ICONS,
    INTENT_TYPE,
} from 'common/constants';
import { COLORS } from 'common/theme';
import { ReactComponent as HaCancelledIcon } from 'common/icons/rounded-slash.svg';
import { ReactComponent as HaPendingIcon } from 'common/icons/3dots-arrow-right-filled.svg';

export interface IHighAvailabilityHistoryStatusProps {
    status: HighAvailabilityStatus;
    compact?: boolean;
    entitiesCount?: {
        total: number;
        failed: number;
        cancelled: number;
    };
}

export const statusMappings: Record<HighAvailabilityStatus, string> = {
    [HighAvailabilityStatus.PENDING]: 'highAvailability.activities.status.pending',
    [HighAvailabilityStatus.RUNNING]: 'highAvailability.activities.status.running',
    [HighAvailabilityStatus.RUNNING_WITH_ERRORS]: 'highAvailability.activities.status.runningWithErrors',
    [HighAvailabilityStatus.COMPLETED]: 'highAvailability.activities.status.completed',
    [HighAvailabilityStatus.COMPLETED_WITH_ERRORS]: 'highAvailability.activities.status.completedWithErrors',
    [HighAvailabilityStatus.FAILED]: 'highAvailability.activities.status.failed',
    [HighAvailabilityStatus.CANCELLED]: 'highAvailability.activities.status.cancelled',
};

const EntityCountsSubtitle: React.FC<{ total: number; failed: number; cancelled: number }> = ({ total, failed, cancelled }) => (
    <Text intent={INTENT_TYPE.MUTED} fontSize="sm">
        {failed > 0 && cancelled > 0 ? (
            <Translate
                content="highAvailability.activities.status.entityCountsFailedAndCancelled"
                params={{ failed, cancelled }}
            />
        ) : failed > 0 ? (
            <Translate
                content="highAvailability.activities.status.entityCountsFailed"
                params={{ failed, total }}
            />
        ) : (
            <Translate
                content="highAvailability.activities.status.entityCountsCancelled"
                params={{ cancelled, total }}
            />
        )}
    </Text>
);

const StatusWithSubtitle: React.FC<{ icon: React.ReactNode; label: React.ReactNode; subtitle?: React.ReactNode }> = ({
    icon,
    label,
    subtitle,
}) => (
    <span style={{ display: 'inline-flex', alignItems: subtitle ? 'flex-start' : 'center', gap: '4px' }}>
        {icon}
        {subtitle ? (
            <span>
                <div>{label}</div>
                {subtitle}
            </span>
        ) : label}
    </span>
);

export const HighAvailabilityHistoryStatusComponent: React.FC<IHighAvailabilityHistoryStatusProps> = ({
    status,
    entitiesCount,
}) => {
    const errorSubtitle = entitiesCount && (entitiesCount.failed > 0 || entitiesCount.cancelled > 0)
        ? <EntityCountsSubtitle total={entitiesCount.total} failed={entitiesCount.failed} cancelled={entitiesCount.cancelled} />
        : undefined;

    if (status === HighAvailabilityStatus.RUNNING) {
        return (
            <Action progress={true}>
                <Translate content={statusMappings[status]} />
            </Action>
        );
    }

    if (status === HighAvailabilityStatus.RUNNING_WITH_ERRORS) {
        return (
            <Action progress={true}>
                <Translate content={statusMappings[status]} />
            </Action>
        );
    }

    if (status === HighAvailabilityStatus.COMPLETED) {
        return (
            <StatusWithSubtitle
                icon={<Icon name={ICONS.CHECK_MARK_CIRCLE_FILLED} size="16" intent={INTENT_TYPE.SUCCESS} />}
                label={<Translate content={statusMappings[status]} />}
            />
        );
    }

    if (status === HighAvailabilityStatus.COMPLETED_WITH_ERRORS) {
        return (
            <StatusWithSubtitle
                icon={<Icon name={ICONS.EXCLAMATION_MARK_CIRCLE_FILLED} size="16" intent={INTENT_TYPE.WARNING} />}
                label={<Translate content={statusMappings[status]} />}
                subtitle={errorSubtitle}
            />
        );
    }

    if (status === HighAvailabilityStatus.FAILED) {
        const isNotEvacuated = entitiesCount && entitiesCount.failed > 0 && entitiesCount.cancelled > 0;
        return (
            <StatusWithSubtitle
                icon={<Icon name={ICONS.CROSS_MARK_CIRCLE_FILLED} size="16" intent={INTENT_TYPE.DANGER} />}
                label={
                    isNotEvacuated
                        ? <Translate content="highAvailability.activities.status.notEvacuated" />
                        : <Translate content={statusMappings[status]} />
                }
                subtitle={errorSubtitle}
            />
        );
    }

    if (status === HighAvailabilityStatus.CANCELLED) {
        return (
            <StatusWithSubtitle
                icon={<span style={{ color: COLORS.GREY_70 }}><HaCancelledIcon width={15} height={15} /></span>}
                label={<Translate content={statusMappings[status]} />}
                subtitle={errorSubtitle}
            />
        );
    }

    return (
        <StatusWithSubtitle
            icon={<span style={{ color: COLORS.INFO }}><HaPendingIcon width={15} height={15} /></span>}
            label={<Translate content={statusMappings[status]} />}
        />
    );
};
